import React from 'react'
import PropTypes from 'prop-types'
import { Alert } from 'react-bootstrap'

import CivWallet from '../../../components/CivWallet/civ-wallet'
import SettingsLayout from '../../../components/Settings/SettingsLayout'
import SettingsCard from '../../../components/Settings/SettingsCard'
import SelectionCard from '../../../components/Settings/SelectionCard'
import SocialMediaCard from '../../account/settings/SocialMediaCard'
import SponsorshipAdMessage from '../sponsorship-ad-message'
import SponsorshipProgram from '../sponsorship-program'
import BusinessVerificationCard from './BusinessVerificationCard'
import BusinessProfileCard from './BusinessProfileCard'
import CollaborationAdminsCard from './CollaborationAdminsCard'
import CommunityMessageCard from './CommunityMessageCard'
import CoverPhotoCard from './CoverPhotoCard'
import SponsorMarketingCard from './SponsorMarketingCard'
import { BUSINESS_SOCIAL_FIELDS } from './constants'
import { businessMediaUrl } from './businessMedia'

/**
 * Presentational layout for the business settings page.
 * All state and handlers come from useBusinessEdit().
 */
export default function BusinessSettingsContent({
  business_id,
  business,
  setBusiness,
  savedEin,
  industries,
  localeKey,
  locale,
  hasBetaFeature,
  businessLoading,
  error,
  setError,
  businessAdmins,
  setIsAdminModalOpen,
  causeOptions,
  breadcrumbs,
  profileSaveDisabled,
  initialSlug,
  checkingSlug,
  slugTaken,
  slugReserved,
  hasSocialNetworksChanged,
  hasMediaChanged,
  hasMessageChanged,
  hasAdminsChanged,
  isSavingSocials,
  hardNormalizeSlug,
  onSaveBusiness,
  onChangeSlug,
  updateBusinessField,
  updateSocialField,
  imageBusinessOnChange,
  handleCoverPhotoChange,
  handleBannerAdGraphicsChange,
  handleRemoveAdmin,
  onSaveAdmins,
  onSaveSocials,
  saveCauses,
  setHasMessageChanged,
  updateBusiness,
  t,
}) {
  const coverImageUrl = businessMediaUrl(
    business,
    'cover_photo',
    'cover_photo_id',
  )
  const bannerImageUrl = businessMediaUrl(
    business,
    'banner_ad_graphics',
    'banner_ad_graphics_id',
  )

  return (
    <SettingsLayout title={t('business.edit.title')} breadcrumbs={breadcrumbs}>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <BusinessVerificationCard businessId={business_id} ein={savedEin} />

      <BusinessProfileCard
        business={business}
        industries={industries}
        localeKey={localeKey}
        onFieldChange={updateBusinessField}
        onChangeSlug={onChangeSlug}
        onAvatarChange={imageBusinessOnChange}
        onSave={onSaveBusiness}
        saveDisabled={profileSaveDisabled}
        isSaving={businessLoading}
        initialSlug={initialSlug}
        checkingSlug={checkingSlug}
        slugTaken={slugTaken}
        slugReserved={slugReserved}
        hardNormalizeSlug={hardNormalizeSlug}
      />

      <CollaborationAdminsCard
        admins={businessAdmins}
        onAddClick={() => setIsAdminModalOpen(true)}
        onRemoveAdmin={handleRemoveAdmin}
        onSave={onSaveAdmins}
        hasChanged={hasAdminsChanged}
        isSaving={businessLoading}
      />

      <CommunityMessageCard
        value={business.community_statement}
        onChange={value => {
          updateBusinessField('community_statement', value)
          setHasMessageChanged(true)
        }}
        onSave={onSaveBusiness}
        hasChanged={hasMessageChanged}
        isSaving={businessLoading}
      />

      <CoverPhotoCard
        imageUrl={coverImageUrl}
        onChange={handleCoverPhotoChange}
        onSave={onSaveBusiness}
        hasChanged={hasMediaChanged}
        isSaving={businessLoading}
      />

      <SponsorMarketingCard
        imageUrl={bannerImageUrl}
        onChange={handleBannerAdGraphicsChange}
        onSave={onSaveBusiness}
        hasChanged={hasMediaChanged}
        isSaving={businessLoading}
      />

      <SelectionCard
        icon="hand"
        title={t('users.edit.causes.title')}
        description={t(
          'business.edit.causes_description',
          'Select causes to support.',
        )}
        options={causeOptions}
        value={business.category_ids ?? []}
        onSave={saveCauses}
        max={3}
        showIcons
        buttonLabel={t('users.edit.select_causes', 'Select Favorite Causes')}
      />

      <SocialMediaCard
        data={business}
        onFieldChange={updateSocialField}
        onSave={onSaveSocials}
        hasChanged={hasSocialNetworksChanged}
        isSaving={isSavingSocials || businessLoading}
        fields={BUSINESS_SOCIAL_FIELDS}
        title={t('users.edit.socials', 'Social Networks')}
      />

      {hasBetaFeature('pf_business') ? (
        <>
          <SettingsCard
            id="wallet"
            icon="wallet"
            title={t('navigation_banner.civ_wallet', 'CIV Wallet')}
          >
            <CivWallet
              data={business}
              setData={setBusiness}
              locale={locale}
              updateData={updateBusiness}
              origin={'business'}
            />
          </SettingsCard>

          <SponsorshipProgram businessData={business} bare />
          <SponsorshipAdMessage businessData={business} bare />
        </>
      ) : null}
    </SettingsLayout>
  )
}

BusinessSettingsContent.propTypes = {
  business_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  business: PropTypes.object.isRequired,
  setBusiness: PropTypes.func.isRequired,
  savedEin: PropTypes.string,
  industries: PropTypes.array,
  localeKey: PropTypes.string.isRequired,
  locale: PropTypes.string,
  hasBetaFeature: PropTypes.func.isRequired,
  businessLoading: PropTypes.bool,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  businessAdmins: PropTypes.array,
  setIsAdminModalOpen: PropTypes.func.isRequired,
  causeOptions: PropTypes.array.isRequired,
  breadcrumbs: PropTypes.array.isRequired,
  profileSaveDisabled: PropTypes.bool,
  initialSlug: PropTypes.string,
  checkingSlug: PropTypes.bool,
  slugTaken: PropTypes.bool,
  slugReserved: PropTypes.bool,
  hasSocialNetworksChanged: PropTypes.bool,
  hasMediaChanged: PropTypes.bool,
  hasMessageChanged: PropTypes.bool,
  hasAdminsChanged: PropTypes.bool,
  isSavingSocials: PropTypes.bool,
  hardNormalizeSlug: PropTypes.func.isRequired,
  onSaveBusiness: PropTypes.func.isRequired,
  onChangeSlug: PropTypes.func.isRequired,
  updateBusinessField: PropTypes.func.isRequired,
  updateSocialField: PropTypes.func.isRequired,
  imageBusinessOnChange: PropTypes.func.isRequired,
  handleCoverPhotoChange: PropTypes.func.isRequired,
  handleBannerAdGraphicsChange: PropTypes.func.isRequired,
  handleRemoveAdmin: PropTypes.func.isRequired,
  onSaveAdmins: PropTypes.func.isRequired,
  onSaveSocials: PropTypes.func.isRequired,
  saveCauses: PropTypes.func.isRequired,
  setHasMessageChanged: PropTypes.func.isRequired,
  updateBusiness: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
}
